import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { MotionConfig, motion } from 'framer-motion';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BucketModal,
  type BucketModalRow,
  type BucketModalStatus,
} from './components/BucketModal';
import { Footer } from './components/Footer';
import { Frame } from './components/Frame';
import { Hero } from './components/Hero';
import {
  HoverTooltip,
  TipDetail,
  TipHeader,
  TipTime,
} from './components/HoverTooltip';
import {
  IndexerPipelineCard,
  type FreshnessEndpoint,
} from './components/IndexerPipelineCard';
import { IndexerLagCards } from './components/IndexerLagCards';
import { EndpointDetailModal } from './components/EndpointDetailModal';
import { LagDetailModal } from './components/LagDetailModal';
import { DatePicker } from './components/DatePicker';
import { SectionHeading } from './components/SectionHeading';
import { StatusPill } from './components/StatusPill';
import {
  bucketSeverity,
  shouldMarkFailures,
} from './lib/bucket-severity';
import { anchoredDayIso, anchorIsoForDay } from './lib/calendar';
import { IncidentDetail } from './components/IncidentDetail';
import { IncidentsList } from './components/IncidentsList';
import { fetchIncidents } from './lib/incidents-api';
import { isTerminalStatus } from './lib/incident-helpers';
import {
  endpointLinkPath,
  parseEndpointKey,
  formatEndpointUrl,
  isFreshnessEndpoint,
  isSyntheticEndpoint,
} from './lib/endpoint-key';
import type { LagWindow } from './lib/lag-time';
import {
  filterGroupsByNetwork,
  type NetworkFilter,
} from './lib/filter-network';
import {
  Check,
  ChevronDown,
  ChevronRight,
  SearchIcon,
  TonCenterLogo,
} from './components/icons';
import { api } from './lib/status-api';
import type {
  ComponentStatus,
  ComponentSummary,
  EndpointSummary,
  UptimeWindow,
  UptimeWindowResponse,
  WindowBucket,
} from './lib/status-types';
import {
  bucketTone,
  deriveOverall,
  formatPct,
  statusTone,
  TONE_BAR,
  type Tone,
} from './lib/status-mapping';
import {
  SUITE_SEVERITY_META,
  suiteSeverity,
  type SuiteSeverity,
} from './lib/suite-severity';
import {
  bucketMsForWindow,
  isPendingBucket,
  padTrailingPending,
  trailingPadNow,
} from './lib/uptime-window';
import {
  formatBucketHoverLabel,
  formatBucketModalTitle,
  formatBucketTimeRange,
  formatWindowEdge,
} from './lib/uptime-format';
import { uptimeQueryKey } from './lib/uptime-api';
import {
  bucketParam,
  incidentParam,
  lagViewParam,
  networkParam,
  parseUrlState,
  patchUrlParams,
  windowParam,
  type BucketLink,
  type LagView,
  type UrlState,
} from './lib/url-state';
import { type ChildTone } from './lib/mock-data';

// ─── App ─────────────────────────────────────────────────────────────

function parseInitialUrlState(): UrlState {
  return parseUrlState(
    typeof window === 'undefined' ? '' : window.location.search,
  );
}

/**
 * `?date=` without an explicit `?window=` restores the full-day view —
 * onPickDate always pairs the anchor with the 24h window, so a hand-trimmed
 * link must not quietly render a 1-hour slice of the picked day.
 */
function initialWindowSel(url: UrlState): UptimeWindow {
  if (url.date === null || typeof window === 'undefined') return url.window;
  return new URLSearchParams(window.location.search).has('window')
    ? url.window
    : '24h';
}

/** DOM id for a service card — the `?service=` deep link scrolls to it. */
function serviceDomId(suiteKey: string): string {
  return `service-${suiteKey}`;
}

/**
 * Delete `?bucket=` only while it still holds `link`'s own value — a late
 * scrub (stale link resolving after the user already opened another bucket
 * modal) must not eat the param that modal's mirror is actively maintaining.
 */
function scrubBucketParamIf(link: BucketLink): void {
  if (typeof window === 'undefined') return;
  const current = parseUrlState(window.location.search).bucket;
  if (
    current !== null &&
    current.service === link.service &&
    current.startAt === link.startAt
  ) {
    patchUrlParams({ bucket: null });
  }
}

function App() {
  // Deep-link state (`?network=…&window=…&incident=…&service=…&lag=…`) is
  // parsed once at mount; from then on state changes are mirrored back into
  // the URL by the effects below, so the address bar is always shareable.
  const [initialUrl] = useState(parseInitialUrlState);
  const [env, setEnv] = useState<NetworkFilter>(initialUrl.network);
  const [swap, setSwap] = useState(false);
  const swapTimer = useRef<number | null>(null);
  const [windowSel, setWindowSel] = useState<UptimeWindow>(() =>
    initialWindowSel(initialUrl),
  );
  // `?date=` deep link restores the DatePicker anchor exactly the way
  // onPickDate would set it: that day's end-of-day, clamped to now.
  const [anchorAt, setAnchorAt] = useState<string | undefined>(() =>
    initialUrl.date !== null
      ? anchorIsoForDay(initialUrl.date, new Date())
      : undefined,
  );
  const [incidentId, setIncidentId] = useState<number | null>(
    initialUrl.incident,
  );
  // Suite key (`key.split('::')[1]`) of the service card expanded via the
  // `?service=` deep link or by the user. Last-opened wins; closing a card
  // only clears the param when that card is the one the URL points at.
  const [expandedService, setExpandedService] = useState<string | null>(
    initialUrl.service,
  );
  // One-shot `?bucket=` deep link, parsed ONCE per page load and handed down
  // to the ServiceCard whose suite key matches. Living at App level (not in
  // the card) means a card remount — network switch, incident round-trip,
  // error→recovery — can never re-read the address bar and replay a link
  // that was already consumed or suppressed. Precedence (url-state.ts
  // header): a `?lag=`/`?lagEndpoint=` link wins over `?bucket` — the DOM
  // dialog guard can't see a sibling's modal opened in the same effects
  // flush, so priority is decided at parse time.
  const [pendingBucket, setPendingBucket] = useState<BucketLink | null>(() =>
    initialUrl.lag !== null || initialUrl.lagEndpoint !== null
      ? null
      : initialUrl.bucket,
  );
  const consumeBucketLink = useCallback(() => {
    setPendingBucket(null);
  }, []);

  // Mirroring runs through patchUrlParams — always replaceState, never
  // compare-first (see lib/url-state.ts for why compare-first was flaky).
  useEffect(() => {
    patchUrlParams({ incident: incidentParam(incidentId) });
  }, [incidentId]);

  useEffect(() => {
    patchUrlParams({ window: windowParam(windowSel) });
  }, [windowSel]);

  useEffect(() => {
    patchUrlParams({ network: networkParam(env) });
  }, [env]);

  useEffect(() => {
    patchUrlParams({ service: expandedService });
  }, [expandedService]);

  useEffect(() => {
    patchUrlParams({
      date: anchorAt !== undefined ? anchoredDayIso(anchorAt) : null,
    });
  }, [anchorAt]);

  // Precedence losers are RETIRED at load, not merely ignored — a suppressed
  // param left in the address bar would resurrect its modal on the next
  // remount that re-parses the URL (network switch, incident round-trip,
  // card collapse/re-expand).
  useEffect(() => {
    const lagWins = initialUrl.lag !== null;
    const scrub: Partial<Record<'bucket' | 'lagEndpoint', null>> = {};
    if (
      initialUrl.bucket !== null &&
      (lagWins || initialUrl.lagEndpoint !== null)
    ) {
      scrub.bucket = null;
    }
    if (initialUrl.lagEndpoint !== null && lagWins) {
      scrub.lagEndpoint = null;
    }
    if (Object.keys(scrub).length > 0) patchUrlParams(scrub);
  }, [initialUrl]);

  // A deep link is glued to the network it was minted on: switching networks
  // cancels an unconsumed `?bucket` link instead of letting the same suite's
  // card on the OTHER network claim it (suite keys and bucket grids are
  // identical across networks). Mirrors IndexerLagCards' pending-link reset.
  const pendingBucketEnvRef = useRef(env);
  useEffect(() => {
    if (pendingBucketEnvRef.current === env) return;
    pendingBucketEnvRef.current = env;
    if (pendingBucket !== null) {
      setPendingBucket(null);
      scrubBucketParamIf(pendingBucket);
    }
  }, [env, pendingBucket]);
  const overall = useQuery({
    queryKey: ['status'],
    queryFn: api.status,
    refetchInterval: 30_000,
  });
  const components = useQuery({
    queryKey: ['components'],
    queryFn: api.components,
    refetchInterval: 30_000,
  });
  const incidents = useQuery({
    queryKey: ['incidents'],
    queryFn: () => fetchIncidents(90),
    refetchInterval: 60_000,
  });
  const selectedIncident = useMemo(() => {
    if (incidentId === null || !incidents.data) return null;
    return incidents.data.incidents.find((i) => i.id === incidentId) ?? null;
  }, [incidentId, incidents.data]);
  const activeIncidents = useMemo(
    () =>
      (incidents.data?.incidents ?? []).filter(
        (i) => !isTerminalStatus(i.status),
      ),
    [incidents.data],
  );

  const visibleGroups = useMemo(
    () => filterGroupsByNetwork(components.data ?? [], env),
    [components.data, env],
  );
  const flatServices = useMemo(
    () => visibleGroups.flatMap((g) => g.components),
    [visibleGroups],
  );

  // A `?bucket` link whose suite has no card on the current network can never
  // be consumed — retire it (and its param) instead of leaving it armed to
  // fire long after load if the suite ever appears.
  useEffect(() => {
    if (pendingBucket === null || components.data === undefined) return;
    const present = flatServices.some(
      (s) => (s.key.split('::')[1] ?? '') === pendingBucket.service,
    );
    if (!present) {
      setPendingBucket(null);
      scrubBucketParamIf(pendingBucket);
    }
  }, [pendingBucket, components.data, flatServices]);

  // Bring a `?service=` deep link into view on the first render that has
  // service cards. Strictly one-shot and best-effort: the attempt is consumed
  // even when the card isn't in the DOM (suite absent on this network, or an
  // `?incident=` deep link showing IncidentDetail instead) — retrying on a
  // later refetch would yank the viewport mid-browsing, long after load.
  const scrolledToService = useRef(false);
  useEffect(() => {
    if (scrolledToService.current || initialUrl.service === null) return;
    if (flatServices.length === 0) return;
    scrolledToService.current = true;
    const el = document.getElementById(serviceDomId(initialUrl.service));
    el?.scrollIntoView({ block: 'start' });
  }, [flatServices, initialUrl.service]);

  const overallStatus = useMemo<ComponentStatus>(
    () =>
      env === 'mainnet' || env === 'testnet'
        ? deriveOverall(flatServices.map((c) => c.status))
        : (overall.data?.status ?? 'no_data'),
    [env, flatServices, overall.data?.status],
  );

  // Each network gets its OWN indicator in the header switcher — computed
  // independently of the currently-viewed network.
  const envTones = useMemo<Record<'mainnet' | 'testnet', Tone>>(() => {
    const tone = (net: 'mainnet' | 'testnet'): Tone =>
      statusTone(
        deriveOverall(
          filterGroupsByNetwork(components.data ?? [], net)
            .flatMap((g) => g.components)
            .map((c) => c.status),
        ),
      );
    return { mainnet: tone('mainnet'), testnet: tone('testnet') };
  }, [components.data]);

  function changeEnv(next: NetworkFilter) {
    if (next === env) return;
    if (swapTimer.current !== null) window.clearTimeout(swapTimer.current);
    setSwap(true);
    swapTimer.current = window.setTimeout(() => {
      setEnv(next);
      setSwap(false);
      swapTimer.current = null;
    }, 160);
  }

  useEffect(
    () => () => {
      if (swapTimer.current !== null) window.clearTimeout(swapTimer.current);
    },
    [],
  );

  return (
    <MotionConfig reducedMotion="user">
      <Header env={env} onChangeEnv={changeEnv} envTones={envTones} />
      <main
        className={clsx(
          'transition-opacity duration-150',
          swap ? 'opacity-0' : 'opacity-100',
        )}
      >
        {selectedIncident ? (
          <Frame
            as="section"
            lines="bottom"
            className="px-4 pb-16 pt-12 min-[768px]:px-[42px] min-[768px]:pb-24 min-[768px]:pt-20"
          >
            <IncidentDetail
              incident={selectedIncident}
              onBack={() => setIncidentId(null)}
            />
          </Frame>
        ) : (
          <>
            <Hero
              status={overallStatus}
              updatedAt={overall.data?.updatedAt ?? null}
              loading={overall.isLoading}
              activeIncidents={activeIncidents}
              onSelectIncident={(id) => {
                setIncidentId(id);
              }}
            />
            <IndexerLagCards
              groups={visibleGroups}
              network={env === 'testnet' ? 'testnet' : 'mainnet'}
            />
            <SectionToolbar
              windowSel={windowSel}
              onChangeWindow={(w) => {
                // Choosing a granularity is a live window — drop any anchored
                // date so 24h doesn't stay highlighted alongside the date.
                setWindowSel(w);
                setAnchorAt(undefined);
              }}
              anchorAt={anchorAt}
              onClearAnchor={() => {
                // The × beside the date returns to the 1-hour live view.
                setAnchorAt(undefined);
                setWindowSel('1h');
              }}
              selectedIso={anchorAt ? anchoredDayIso(anchorAt) : null}
              onPickDate={(dayIso) => {
                // Drill into the chosen day: anchor the window at that day's
                // end-of-day (clamped to now) and switch to the 24h view.
                setAnchorAt(anchorIsoForDay(dayIso, new Date()));
                setWindowSel('24h');
              }}
            />
            <Frame lines="both" className="bg-paper-1">
              {components.isLoading ? (
                <ServicesSkeleton />
              ) : components.error ? (
                <ErrorBlock message={(components.error).message} />
              ) : flatServices.length === 0 ? (
                <EmptyBlock />
              ) : (
                flatServices.map((service) => {
                  const suiteKey = service.key.split('::')[1] ?? '';
                  return (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      windowSel={windowSel}
                      anchorAt={anchorAt}
                      pendingBucket={pendingBucket}
                      onConsumeBucketLink={consumeBucketLink}
                      network={env === 'testnet' ? 'testnet' : 'mainnet'}
                      initialOpen={
                        suiteKey !== '' && expandedService === suiteKey
                      }
                      onOpenChange={(open) => {
                        setExpandedService((prev) =>
                          open
                            ? suiteKey
                            : prev === suiteKey
                              ? null
                              : prev,
                        );
                      }}
                    />
                  );
                })
              )}
            </Frame>
            <IncidentsList
              data={incidents.data}
              loading={incidents.isLoading}
              error={(incidents.error as Error | null) ?? null}
              onSelect={(id) => setIncidentId(id)}
            />
          </>
        )}
      </main>
      {/* toncenter.com `footerSpacer`: rails run on down to the footer. */}
      <Frame aria-hidden className="h-[120px] min-[992px]:h-[176px]" />
      <Footer />
    </MotionConfig>
  );
}

// ─── Header ──────────────────────────────────────────────────────────

function Header({
  env,
  onChangeEnv,
  envTones,
}: {
  env: NetworkFilter;
  onChangeEnv: (next: NetworkFilter) => void;
  envTones: Record<'mainnet' | 'testnet', Tone>;
}) {
  // toncenter.com header geometry: 80px bar (56px ≤767px), sticky from
  // 768px, hairline + junctions on its bottom edge. Left: the TON Center
  // wordmark (→ toncenter.com) + "Health"; right: the network switch in the
  // slot toncenter.com uses for its button. No centre nav (DESIGN.md §7).
  return (
    <Frame
      as="header"
      lines="bottom"
      bandClassName="z-30 bg-paper-2 min-[768px]:sticky min-[768px]:top-0"
      className="px-3 min-[992px]:px-[42px]"
    >
      <div className="flex min-h-[56px] items-center justify-between gap-3 min-[768px]:min-h-[80px]">
        <div className="flex min-w-0 items-center gap-2 min-[768px]:gap-4">
          <a
            href="https://toncenter.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TON Center — open toncenter.com"
            className="block flex-shrink-0 rounded-sm text-ink-1 hover:text-ink-1"
          >
            <TonCenterLogo className="h-6 min-[768px]:h-7" />
          </a>
          <span aria-hidden className="h-5 w-px flex-shrink-0 bg-line-strong" />
          <span className="whitespace-nowrap text-[15px] font-medium leading-none text-ink-3 min-[768px]:text-[18px]">
            Health
          </span>
        </div>
        <EnvSelect value={env} onChange={onChangeEnv} tones={envTones} />
      </div>
    </Frame>
  );
}

// ─── StatusDot — pulsing green when ok, blinking amber otherwise ────

function StatusDot({ tone, small = false }: { tone: Tone; small?: boolean }) {
  const size = small ? 'h-2 w-2' : 'h-2 w-2';
  // ok: solid pulse (calm breathing). warn/down/none: dot + ping ring (alert).
  const isOk = tone === 'ok';
  const dotColor =
    tone === 'ok'
      ? 'bg-ok'
      : tone === 'down'
        ? 'bg-down'
        : tone === 'none'
          ? 'bg-line-strong'
          : 'bg-warn';
  return (
    <span
      className={clsx('relative inline-flex flex-shrink-0', size)}
      aria-hidden
    >
      {!isOk && tone !== 'none' && (
        <span
          className={clsx(
            'absolute inset-0 inline-flex animate-ping rounded-full opacity-70',
            dotColor,
          )}
        />
      )}
      <span
        className={clsx(
          'relative inline-flex rounded-full',
          size,
          dotColor,
          isOk && 'animate-pulse',
        )}
      />
    </span>
  );
}

// ─── EnvSelect (custom popup, per design) ───────────────────────────

interface EnvOption {
  value: 'mainnet' | 'testnet';
  label: string;
  hint: string;
}

const ENV_OPTIONS: EnvOption[] = [
  { value: 'mainnet', label: 'Mainnet', hint: 'toncenter.com' },
  {
    value: 'testnet',
    label: 'Testnet',
    hint: 'testnet.toncenter.com',
  },
];

function EnvSelect({
  value,
  onChange,
  tones,
}: {
  value: NetworkFilter;
  onChange: (next: NetworkFilter) => void;
  tones: Record<'mainnet' | 'testnet', Tone>;
}) {
  const currentTone = tones[value === 'testnet' ? 'testnet' : 'mainnet'];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function onDoc(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const current =
    ENV_OPTIONS.find((o) => o.value === value) ?? ENV_OPTIONS[0];

  return (
    <div ref={ref} className="relative inline-flex flex-shrink-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={clsx(
          // toncenter.com pill control (the header button slot).
          'inline-flex min-h-[36px] flex-shrink-0 items-center justify-between gap-1.5 rounded-full pl-3 pr-2.5 min-[768px]:min-h-[40px] min-[768px]:min-w-[148px] min-[768px]:gap-3 min-[768px]:pl-[18px] min-[768px]:pr-4',
          'text-[14px] font-semibold leading-6 text-ink-1 min-[768px]:text-[16px]',
          'transition-[background-color,transform] duration-150 active:translate-y-px',
          open
            ? 'bg-[rgb(var(--control-active))]'
            : 'bg-[rgb(var(--control))] hover:bg-[rgb(var(--control-active))]',
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-2.5">
          <StatusDot tone={currentTone} />
          <span className="truncate">{current.label}</span>
        </span>
        <ChevronDown
          className={clsx(
            'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150',
            open ? 'rotate-180 text-ink-1' : 'text-ink-3',
          )}
        />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-[calc(100%+8px)] z-30 min-w-[232px] rounded-lg border border-line-strong bg-paper-3 p-1.5 shadow-pop"
        >
          {ENV_OPTIONS.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                role="option"
                type="button"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={clsx(
                  'flex w-full items-center gap-2.5 rounded-md border-0 bg-transparent px-3 py-2.5',
                  'text-left text-[14px] font-medium leading-tight text-ink-1',
                  'whitespace-nowrap transition-colors duration-100',
                  selected
                    ? 'bg-[rgb(var(--control-active))]'
                    : 'hover:bg-[rgb(var(--control))]',
                )}
              >
                <StatusDot tone={tones[option.value]} small />
                <span className="min-w-0 flex-1 pr-4">
                  <span className="block font-semibold leading-tight">
                    {option.label}
                  </span>
                  <span className="mt-1 block font-mono text-[12px] tracking-[-.017em] text-ink-3">
                    {option.hint}
                  </span>
                </span>
                {selected && <Check className="h-3.5 w-3.5 text-accent" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Section toolbar ────────────────────────────────────────────────

function SectionToolbar({
  windowSel,
  onChangeWindow,
  anchorAt,
  onClearAnchor,
  selectedIso,
  onPickDate,
}: {
  windowSel: UptimeWindow;
  onChangeWindow: (w: UptimeWindow) => void;
  anchorAt?: string;
  onClearAnchor: () => void;
  selectedIso: string | null;
  onPickDate: (dayIso: string) => void;
}) {
  // toncenter.com section heading block: rails only, no hairlines — the
  // service list below draws the line under it.
  return (
    <Frame
      as="div"
      className="flex flex-wrap items-end justify-between gap-x-6 gap-y-6 px-4 pb-7 pt-[88px] min-[768px]:px-[42px] min-[768px]:pb-8 min-[768px]:pt-[120px]"
    >
      <SectionHeading label="Services" title="Current health by service" />
      <div className="flex flex-wrap items-center gap-2">
        {/* While a date is anchored no granularity is "active" — the date is
            the selection. Clicking any window radio clears the date. */}
        <WindowSelect
          value={anchorAt ? null : windowSel}
          onChange={onChangeWindow}
        />
        <DatePicker
          selectedIso={selectedIso}
          onPickDate={onPickDate}
          onClear={onClearAnchor}
        />
      </div>
    </Frame>
  );
}

function WindowSelect({
  value,
  onChange,
}: {
  // `null` = no granularity active (a specific date is anchored instead).
  value: UptimeWindow | null;
  onChange: (w: UptimeWindow) => void;
}) {
  const options: readonly { value: UptimeWindow; label: string }[] = [
    { value: '1h', label: '1 hour' },
    { value: '24h', label: '24 hours' },
    { value: '90d', label: '90 days' },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Time window"
      className="relative inline-flex items-center gap-2"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => {
              onChange(opt.value);
            }}
            className={clsx(
              // toncenter.com selector pill.
              'relative inline-flex h-9 items-center rounded-full bg-[rgb(var(--control))] px-4 text-[14px] font-semibold leading-6 transition-colors duration-150',
              active ? 'text-ink-1' : 'text-ink-2 hover:text-ink-1',
            )}
          >
            {active && (
              <motion.span
                layoutId="window-select-active"
                className="absolute inset-0 z-0 rounded-full bg-[rgb(var(--control-active))]"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                aria-hidden
              />
            )}
            <span className="relative z-10">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Service card ───────────────────────────────────────────────────

function ServiceCard({
  service,
  network,
  windowSel,
  anchorAt,
  pendingBucket,
  onConsumeBucketLink,
  initialOpen = false,
  onOpenChange,
}: {
  service: ComponentSummary;
  network: 'mainnet' | 'testnet';
  windowSel: UptimeWindow;
  anchorAt?: string;
  /** App-level one-shot `?bucket=` link — claimed when the suite key matches. */
  pendingBucket: BucketLink | null;
  /** Mark the `?bucket=` link consumed so no later remount can replay it. */
  onConsumeBucketLink: () => void;
  /** `?service=` deep link: mount with the endpoints block expanded. */
  initialOpen?: boolean;
  /** Notify on expand/collapse so App can mirror it into the URL. */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(initialOpen);
  // The chevron flips on `open` (urgent); the heavy endpoints block mounts on
  // the deferred copy so the click feels instant even with 25+ probes inside.
  const deferredOpen = useDeferredValue(open);
  const uptime = useQuery({
    queryKey: uptimeQueryKey(service.id, windowSel, anchorAt ?? ''),
    queryFn: () => api.uptime(service.id, windowSel, anchorAt),
    refetchInterval: windowSel === '1h' ? 30_000 : 60_000,
    staleTime: windowSel === '1h' ? 15_000 : 30_000,
  });
  const suiteKeyShort = service.key.split('::')[1] ?? '';
  const overallPct =
    uptime.data?.overallSuccessPct ?? service.uptimePct90d ?? 0;
  // Suite colour + label follow the success % directly (≥95% → operational
  // green, 80–95% → minor outage amber, 50–80% → major outage orange, <50% →
  // service down red). `no_data` only when the backend reports nothing — a
  // real 0% is "service down", not "no data". Retune in lib/suite-severity.ts.
  const severity: SuiteSeverity =
    service.status === 'no_data' ? 'no_data' : suiteSeverity(overallPct);
  const [modalIdx, setModalIdx] = useState<number | null>(null);
  // One-shot `?bucket=` deep link, claimed from App-level state (parsed once
  // per page load) by the card whose suite key matches. Parks here until the
  // uptime payload arrives, then resolves startAt → bucket index.
  const bucketLink =
    pendingBucket !== null && pendingBucket.service === suiteKeyShort
      ? pendingBucket
      : null;

  useEffect(() => {
    if (bucketLink === null || !uptime.data) return;
    // Consume even when the bucket is gone — live windows roll forward, and
    // retrying against every refetch would pop the modal minutes later.
    onConsumeBucketLink();
    const idx = uptime.data.buckets.findIndex(
      (b) => b.startAt === bucketLink.startAt,
    );
    if (idx !== -1 && document.querySelector('[role="dialog"]') === null) {
      setModalIdx(idx);
      return;
    }
    // The link can't be honored — the bucket rolled out of the window, or
    // another modal already owns the screen. Retire its param too, unless it
    // now mirrors an open modal of this very card.
    if (modalIdx === null) scrubBucketParamIf(bucketLink);
  }, [bucketLink, uptime.data, onConsumeBucketLink, modalIdx]);

  // Reset modal whenever the underlying bucket grid changes — the index would
  // otherwise point at the wrong slice. No URL scrub here: the mirror effect
  // below deletes the param once the modal leaves the screen.
  useEffect(() => {
    setModalIdx(null);
  }, [windowSel, anchorAt]);

  const modalBucket =
    modalIdx !== null && uptime.data ? uptime.data.buckets[modalIdx] : null;

  // Mirror the DISPLAYED bucket into the URL — not the click. A click can
  // land on a shimmer cell before the uptime payload arrives (the modal then
  // materializes when data lands), and on live windows the grid slides under
  // an open modal; keying off `modalBucket` keeps the param honest in both
  // cases. The mirror owns the param's WHOLE lifecycle for this card: it also
  // deletes it once the modal leaves the screen, so a transient wrong value
  // (stale modalIdx over a freshly-cached grid during a window switch) is
  // corrected in the very next flush instead of surviving in the address bar.
  const wroteBucketRef = useRef(false);
  useEffect(() => {
    if (
      modalBucket != null &&
      modalBucket.startAt !== '' &&
      suiteKeyShort !== ''
    ) {
      wroteBucketRef.current = true;
      patchUrlParams({
        bucket: bucketParam(suiteKeyShort, modalBucket.startAt),
      });
    } else if (wroteBucketRef.current) {
      wroteBucketRef.current = false;
      patchUrlParams({ bucket: null });
    }
  }, [modalBucket, suiteKeyShort]);

  // A network switch unmounts the card together with its open modal — scrub
  // the param so the URL doesn't keep promising a modal that isn't on screen.
  useEffect(
    () => () => {
      if (wroteBucketRef.current) {
        patchUrlParams({ bucket: null });
      }
    },
    [],
  );
  const modalRows = useMemo<BucketModalRow[]>(() => {
    if (modalIdx === null || !uptime.data) return [];
    return uptime.data.endpoints.map((ep) => {
      const b = ep.statusBuckets?.[modalIdx];
      return {
        endpointKey: ep.endpointKey,
        status: (b?.status ?? 'no_data') as BucketModalStatus,
      };
    });
  }, [modalIdx, uptime.data]);

  // A suite with no per-endpoint rows (e.g. transaction-monitor) has nothing
  // to reveal — drop the chevron and the toggle so it doesn't look expandable.
  const expandable = !uptime.data || uptime.data.endpoints.length > 0;

  const headerBody = (
    <>
      <div className="min-w-0">
        <p className="m-0 truncate text-[16px] font-semibold leading-none text-ink-1 sm:text-[18px] sm:font-medium">
          {service.displayName}
        </p>
        <p className="m-0 mt-1.5 truncate text-[13px] leading-none text-ink-3 sm:text-[14px]">
          {describeService(service)}
        </p>
      </div>
      <StatusPill
        severity={severity}
        label={severity === 'no_data' ? formatPct(overallPct) : undefined}
        value={severity === 'no_data' ? undefined : formatPct(overallPct)}
      />
    </>
  );

  const headerGrid =
    'grid w-full select-none grid-cols-[18px_1fr_auto] items-center gap-2.5 px-4 pb-3 pt-5 text-left sm:grid-cols-[22px_1fr_auto] sm:gap-3.5 sm:px-5 sm:pb-3.5 sm:pt-6';

  return (
    <div
      id={suiteKeyShort !== '' ? serviceDomId(suiteKeyShort) : undefined}
      className={clsx(
        // A catalog row of the toncenter.com table: surface fill, hairline
        // between rows, no radius. Sticky header (80px) + breathing room.
        'border-b border-line bg-paper-1 last:border-b-0',
        'scroll-mt-4 min-[768px]:scroll-mt-24',
      )}
    >
      {expandable ? (
        <button
          type="button"
          onClick={() => {
            const next = !open;
            setOpen(next);
            onOpenChange?.(next);
          }}
          aria-expanded={open}
          className={clsx(headerGrid, 'cursor-pointer')}
        >
          <ChevronRight
            className={clsx(
              'h-3.5 w-3.5 transition-transform duration-200',
              open ? 'rotate-90 text-ink-2' : 'text-ink-3',
            )}
          />
          {headerBody}
        </button>
      ) : (
        <div className={headerGrid}>
          <span aria-hidden />
          {headerBody}
        </div>
      )}
      <UptimeBlock
        severity={severity}
        windowSel={windowSel}
        uptime={uptime.data}
        isLoading={uptime.isLoading || (!uptime.data && uptime.isFetching)}
        onCellClick={setModalIdx}
      />
      {open && (
        uptime.data && uptime.data.endpoints.length > 0 ? (
          deferredOpen ? (
            <EndpointsBlock
              endpoints={uptime.data.endpoints}
              suiteKey={suiteKeyShort}
              network={network}
              windowSel={windowSel}
              bucketMs={uptime.data.bucketMs}
              componentId={service.id}
              serviceName={service.displayName}
            />
          ) : (
            <EndpointsSkeleton count={uptime.data.endpoints.length} />
          )
        ) : uptime.isLoading || uptime.isFetching ? (
          <EndpointsSkeleton count={6} />
        ) : null
      )}
      {modalBucket && (
        <BucketModal
          isOpen
          onClose={() => {
            setModalIdx(null);
          }}
          title={formatBucketModalTitle(modalBucket, windowSel)}
          subtitle={`${service.displayName} · ${service.network}`}
          overall={bucketSeverity(modalBucket, windowSel)}
          rows={modalRows}
        />
      )}
    </div>
  );
}

const SEARCH_THRESHOLD = 4;

// Visible placeholder while React commits the expanded EndpointsBlock for the
// first time. Same vertical rhythm and column grid as the real rows so the
// layout doesn't jump when the real content takes over. Capped at 8 rows so
// huge suites (29-endpoint V2) don't create a tall flash.
function EndpointsSkeleton({ count }: { count: number }) {
  const rows = Math.min(count, 8);
  return (
    <div
      className="border-t border-line bg-paper-3 px-4 pt-1 pb-2 sm:px-5 sm:pl-12"
      aria-busy="true"
      aria-label="Loading endpoints"
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className={clsx(
            'flex flex-col py-3.5',
            i !== rows - 1 && 'border-b border-line',
          )}
        >
          <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3.5">
            <div className="skeleton-shine h-4 w-9 rounded" />
            <div className="min-w-0 space-y-1.5">
              <div className="skeleton-shine h-3.5 w-2/5 rounded" />
              <div className="skeleton-shine h-3 w-3/5 rounded" />
            </div>
            <div className="skeleton-shine h-3.5 w-10 rounded" />
            <div className="skeleton-shine h-3.5 w-3.5 rounded-full" />
          </div>
          <div className="skeleton-shine mt-2 h-7 rounded" />
        </div>
      ))}
    </div>
  );
}

interface FreshnessDetail {
  endpointKey: string;
  view: LagView;
  window: LagWindow;
}

interface PendingFreshnessLink {
  path: string;
  view: LagView;
  /** `null` — no explicit `?lagWindow=`, the modal follows the page window. */
  window: LagWindow | null;
}

/** Shareable identifier for a freshness endpoint: its display path. */
// Shared with the OG preview backend via lib/endpoint-key — see
// endpointLinkPath. Alias kept for readability at the freshness call sites.
const freshnessLinkPath = endpointLinkPath;

/**
 * `?lagEndpoint=` deep link, read from the CURRENT URL at mount (same
 * rationale as IndexerLagCards' parseLagDeepLink: this block unmounts and
 * remounts with its card, and a page-load snapshot would replay consumed
 * links). Only meaningful together with `?service=` — the block exists only
 * inside an expanded card, and it matches against its own endpoints.
 */
function parseFreshnessDeepLink(): PendingFreshnessLink | null {
  if (typeof window === 'undefined') return null;
  const state = parseUrlState(window.location.search);
  if (state.lagEndpoint === null) return null;
  // Modal-precedence rule (url-state.ts header): `?lag=` outranks
  // `?lagEndpoint=` — see the pendingBucket comment for why the DOM guard
  // alone can't serialize same-flush opens.
  if (state.lag !== null) return null;
  const explicitWindow = new URLSearchParams(window.location.search).has(
    'lagWindow',
  );
  return {
    path: state.lagEndpoint,
    view: state.lagView,
    window: explicitWindow ? state.lagWindow : null,
  };
}

function EndpointsBlock({
  endpoints,
  network,
  windowSel,
  bucketMs,
  componentId,
  serviceName,
}: {
  endpoints: readonly EndpointSummary[];
  suiteKey: string;
  network: 'mainnet' | 'testnet';
  windowSel: UptimeWindow;
  bucketMs?: number;
  componentId: number;
  serviceName: string;
}) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  // Synthetic probes (txlifecycle inspectors under `/lifecycle/_*`) borrow
  // the `<METHOD> <path>` title shape for storage but aren't API endpoints.
  // Their pass/fail is already reflected in the suite-level uptime bar
  // above; showing them in the endpoint list misleads users into thinking
  // toncenter exposes `POST /lifecycle/_cycle-status`.
  const realEndpoints = useMemo(
    () => endpoints.filter((ep) => !isSyntheticEndpoint(ep.endpointKey)),
    [endpoints],
  );
  const showSearch = realEndpoints.length >= SEARCH_THRESHOLD;
  const normalized = deferredQuery.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      normalized === ''
        ? realEndpoints
        : realEndpoints.filter((ep) =>
            ep.endpointKey.toLowerCase().includes(normalized),
          ),
    [realEndpoints, normalized],
  );
  // Split freshness probes (`(freshness)` suffix) from the regular list.
  // Freshness probes render in `IndexerPipelineCard` above the rest, with
  // numerical lag readouts; ordinary probes keep the existing layout.
  const { freshness, regular } = useMemo(() => {
    const f: FreshnessEndpoint[] = [];
    const r: EndpointSummary[] = [];
    for (const ep of filtered) {
      if (isFreshnessEndpoint(ep.endpointKey)) {
        f.push({
          endpointKey: ep.endpointKey,
          status: ep.status,
          currentLagSec: ep.currentLagSec ?? null,
          avgLagSec: ep.avgLagSec ?? null,
          p95LagSec: ep.p95LagSec ?? null,
          maxLagSec: ep.maxLagSec ?? null,
          currentLagBlocks: ep.currentLagBlocks ?? null,
          avgLagBlocks: ep.avgLagBlocks ?? null,
          p95LagBlocks: ep.p95LagBlocks ?? null,
          maxLagBlocks: ep.maxLagBlocks ?? null,
          lagBuckets: ep.lagBuckets ?? null,
        });
      } else {
        r.push(ep);
      }
    }
    return { freshness: f, regular: r };
  }, [filtered]);
  const isFiltering = normalized !== '';
  const noMatches = isFiltering && filtered.length === 0;
  const [freshnessDetail, setFreshnessDetail] =
    useState<FreshnessDetail | null>(null);
  // One-shot `?lagEndpoint=` deep link — parks here until the matching
  // freshness endpoint arrives in this card's uptime payload. Explicit user
  // open/close cancels it, same contract as IndexerLagCards' pending link.
  const [pendingFreshness, setPendingFreshness] =
    useState<PendingFreshnessLink | null>(parseFreshnessDeepLink);

  const openFreshnessDetail = useCallback(
    (
      endpointKey: string,
      view: LagView,
      modalWindow: LagWindow,
      // What to write into `?lagWindow=`: null keeps the modal following the
      // page window (recoverable from `?window=`), a value pins it.
      urlWindow: LagWindow | null,
    ) => {
      setPendingFreshness(null);
      setFreshnessDetail({ endpointKey, view, window: modalWindow });
      patchUrlParams({
        lagEndpoint: freshnessLinkPath(endpointKey),
        lagView: lagViewParam(view),
        lagWindow: urlWindow,
      });
    },
    [],
  );

  const closeFreshnessDetail = useCallback(() => {
    setPendingFreshness(null);
    setFreshnessDetail(null);
    patchUrlParams({ lagEndpoint: null, lagView: null, lagWindow: null });
  }, []);

  // Ordinary (non-freshness) endpoints get a lighter detail modal — its
  // whole point is the shareable `?lagEndpoint=` link (OG previews included).
  const [endpointDetail, setEndpointDetail] = useState<string | null>(null);

  const openEndpointDetail = useCallback((endpointKey: string) => {
    setPendingFreshness(null);
    setEndpointDetail(endpointKey);
    patchUrlParams({ lagEndpoint: endpointLinkPath(endpointKey) });
  }, []);

  const closeEndpointDetail = useCallback(() => {
    setPendingFreshness(null);
    setEndpointDetail(null);
    patchUrlParams({ lagEndpoint: null, lagView: null, lagWindow: null });
  }, []);

  useEffect(() => {
    if (pendingFreshness === null) return;
    const matchFreshness = freshness.find(
      (ep) => freshnessLinkPath(ep.endpointKey) === pendingFreshness.path,
    );
    const matchRegular = matchFreshness
      ? undefined
      : regular.find(
          (ep) => endpointLinkPath(ep.endpointKey) === pendingFreshness.path,
        );
    if (!matchFreshness && !matchRegular) return;
    setPendingFreshness(null);
    // Same stacking guard as IndexerLagCards: if some modal is already open,
    // the deep link lost the race — cancel instead of mounting a second shell.
    if (document.querySelector('[role="dialog"]') !== null) return;
    if (matchFreshness) {
      openFreshnessDetail(
        matchFreshness.endpointKey,
        pendingFreshness.view,
        pendingFreshness.window ?? windowSel,
        pendingFreshness.window,
      );
    } else if (matchRegular) {
      setEndpointDetail(matchRegular.endpointKey);
    }
  }, [pendingFreshness, freshness, regular, windowSel, openFreshnessDetail]);

  // Mirror ServiceCard's modalIdx reset: drop the open detail when the page
  // window changes (the bucket grid underneath it changes shape). Ref-guarded
  // so the mount-time run doesn't erase an unconsumed deep link's params.
  const prevWindowSel = useRef(windowSel);
  useEffect(() => {
    if (prevWindowSel.current === windowSel) return;
    prevWindowSel.current = windowSel;
    closeFreshnessDetail();
  }, [windowSel, closeFreshnessDetail]);

  // The card collapsing (or a network switch) unmounts this block together
  // with its open modal — scrub the modal's params so the URL doesn't keep
  // promising a modal that is no longer on screen.
  const detailOpenRef = useRef(false);
  useEffect(() => {
    detailOpenRef.current = freshnessDetail !== null || endpointDetail !== null;
  }, [freshnessDetail, endpointDetail]);
  useEffect(
    () => () => {
      if (detailOpenRef.current) {
        patchUrlParams({ lagEndpoint: null, lagView: null, lagWindow: null });
      }
    },
    [],
  );
  // Resolved against the UNfiltered list so typing in the search box can't
  // yank an open modal out from under the user.
  const endpointDetailEp =
    endpointDetail === null
      ? null
      : (realEndpoints.find((ep) => ep.endpointKey === endpointDetail) ?? null);
  return (
    <>
      {showSearch && (
        <EndpointSearch
          value={query}
          onChange={setQuery}
          matched={filtered.length}
          total={realEndpoints.length}
        />
      )}
      {!noMatches && (
        <IndexerPipelineCard
          endpoints={freshness}
          windowSel={windowSel}
          onOpenDetail={(endpointKey) => {
            openFreshnessDetail(endpointKey, 'bar', windowSel, null);
          }}
        />
      )}
      {regular.length > 0 && (
        <div className="border-t border-line bg-paper-3 px-4 pt-1 pb-2 sm:px-5 sm:pl-12">
          {regular.map((ep, idx) => (
            <EndpointRow
              key={ep.endpointKey}
              ep={ep}
              isLast={idx === regular.length - 1}
              network={network}
              windowSel={windowSel}
              bucketMs={bucketMs}
              onOpen={openEndpointDetail}
            />
          ))}
        </div>
      )}
      {noMatches && (
        <div className="border-t border-line bg-paper-3 px-4 py-4 text-center text-[12.5px] text-ink-3 sm:px-5 sm:pl-12">
          No endpoints match <span className="font-mono">"{query}"</span>.
        </div>
      )}
      {freshnessDetail !== null && (
        <LagDetailModal
          key={freshnessDetail.endpointKey}
          componentId={componentId}
          endpointKey={freshnessDetail.endpointKey}
          title={serviceName}
          network={network}
          defaultWindow={freshnessDetail.window}
          defaultView={freshnessDetail.view}
          onViewChange={(view) => {
            patchUrlParams({ lagView: lagViewParam(view) });
          }}
          onWindowChange={(modalWindow) => {
            // Written raw (no default-dropping): the modal's natural default
            // is the page window, so an explicit 1h pick must stick too.
            patchUrlParams({ lagWindow: modalWindow });
          }}
          onClose={closeFreshnessDetail}
        />
      )}
      {endpointDetailEp !== null && (
        <EndpointDetailModal
          key={endpointDetailEp.endpointKey}
          ep={endpointDetailEp}
          network={network}
          serviceName={serviceName}
          windowSel={windowSel}
          historyBar={
            endpointDetailEp.statusBuckets &&
            endpointDetailEp.statusBuckets.length > 0 ? (
              <StatusHistoryBar
                buckets={endpointDetailEp.statusBuckets}
                windowSel={windowSel}
                endpointKey={endpointDetailEp.endpointKey}
                bucketMs={bucketMs}
              />
            ) : null
          }
          onClose={closeEndpointDetail}
        />
      )}
    </>
  );
}

const EndpointRow = memo(function EndpointRow({
  ep,
  isLast,
  network,
  windowSel,
  bucketMs,
  onOpen,
}: {
  ep: EndpointSummary;
  isLast: boolean;
  network: 'mainnet' | 'testnet';
  windowSel: UptimeWindow;
  bucketMs?: number;
  onOpen: (endpointKey: string) => void;
}) {
  const parsed = parseEndpointKey(ep.endpointKey);
  const url = formatEndpointUrl({ ...parsed, network });
  // Synthetic probes (e.g. `POST /lifecycle/_cycle-status`) are inspectors
  // of cycle state, not HTTP endpoints. Suppress the method badge — the
  // URL line is already empty by `formatEndpointUrl`.
  const showMethod = parsed.method && !isSyntheticEndpoint(ep.endpointKey);
  const tone: ChildTone = bucketTone(bucketSeverity(ep, windowSel));
  return (
    <div
      className={clsx(
        'flex flex-col py-3',
        !isLast && 'border-b border-line',
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(ep.endpointKey)}
        aria-label={`Open ${parsed.path} details`}
        className="-mx-1.5 grid w-[calc(100%+0.75rem)] cursor-pointer grid-cols-[auto_1fr_auto_auto] items-center gap-2 rounded-md px-1.5 py-0.5 text-left text-[13.5px] transition-colors hover:bg-[rgb(var(--control))] sm:gap-3.5 sm:text-[14px]"
      >
        {showMethod ? (
          <span className="rounded border border-line-strong bg-paper-1 px-1.5 py-0.5 font-mono text-[10.5px] text-ink-2">
            {parsed.method}
          </span>
        ) : (
          <span aria-hidden />
        )}
        <div className="min-w-0">
          <div className="truncate font-medium text-ink-1">{parsed.path}</div>
          {url && (
            <div className="mt-1 hidden truncate font-mono text-[11.5px] tracking-[-.01em] text-ink-3 sm:block">
              {url}
            </div>
          )}
        </div>
        <div className="text-[12px] font-semibold text-ink-2 [font-feature-settings:'tnum']">
          {formatPct(ep.successPct)}
        </div>
        <ChildStatus tone={tone} />
      </button>
      {ep.statusBuckets && ep.statusBuckets.length > 0 && (
        <div className="mt-2">
          <StatusHistoryBar
            buckets={ep.statusBuckets}
            windowSel={windowSel}
            endpointKey={ep.endpointKey}
            bucketMs={bucketMs}
          />
        </div>
      )}
    </div>
  );
});

function EndpointSearch({
  value,
  onChange,
  matched,
  total,
}: {
  value: string;
  onChange: (next: string) => void;
  matched: number;
  total: number;
}) {
  const filtering = value.trim() !== '';
  return (
    <div className="border-t border-line bg-paper-3 px-4 py-2.5 sm:px-5 sm:pl-12">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
        <input
          type="search"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && value !== '') {
              e.preventDefault();
              onChange('');
            }
          }}
          placeholder="Filter endpoints…"
          aria-label="Filter endpoints"
          className={clsx(
            'block w-full rounded-full border border-line-strong bg-paper-1 py-2 pl-8 pr-20',
            'text-[14px] leading-none text-ink-1 placeholder:text-ink-3',
            'transition-[border-color,box-shadow] duration-100',
            'focus:border-accent focus:outline-none',
          )}
        />
        {filtering && (
          <div className="pointer-events-none absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-2">
            <span className="text-[11.5px] font-medium text-ink-3 [font-feature-settings:'tnum']">
              {matched}/{total}
            </span>
            <button
              type="button"
              onClick={() => {
                onChange('');
              }}
              aria-label="Clear filter"
              className="pointer-events-auto rounded p-0.5 text-ink-3 transition-colors hover:bg-[rgb(var(--control))] hover:text-ink-1"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3 w-3"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CAP-3 of `.claude/spec/status-day-cell-severity.md`: a day that renders green
 * but did have failures carries a hairline in the `down` hue along its bottom
 * edge. The colour calms down, the evidence stays — this is what keeps the
 * softened ladder honest. Atlassian does the same thing by nudging any declared
 * outage ~35-45% of the way from green to red, so a day with an incident can
 * never look identical to a clean one.
 *
 * It is an overlay, not a change of the cell's height: DESIGN.md §6's "no
 * height variation by status" still holds for the bar itself.
 *
 * Decorative only — the counts live in the tooltip, so it is aria-hidden.
 */
function FailureMark() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] rounded-b-[2.5px] bg-down"
    />
  );
}

function ChildStatus({ tone }: { tone: ChildTone }) {
  const label =
    tone === 'ok'
      ? 'Operational'
      : tone === 'warn'
        ? 'Degraded'
        : tone === 'major'
          ? 'Heavily degraded'
          : tone === 'down'
            ? 'Partial outage'
            : 'No data';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 text-[12px] font-semibold',
        tone === 'ok' && 'text-ok-fg',
        tone === 'warn' && 'text-warn-fg',
        tone === 'major' && 'text-major-fg',
        tone === 'down' && 'text-down-fg',
        tone === 'none' && 'text-ink-3',
      )}
      aria-label={label}
    >
      <span
        className={clsx(
          'inline-block h-2 w-2 rounded-full sm:h-1.5 sm:w-1.5',
          tone === 'ok' && 'bg-ok',
          tone === 'warn' && 'bg-warn',
          tone === 'major' && 'bg-major',
          tone === 'down' && 'bg-down',
          tone === 'none' && 'bg-line-strong',
        )}
      />
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}

function describeService(service: ComponentSummary): string {
  return `${service.network} · ${service.group}`;
}

// ─── Uptime block (90-day bars + foot) ──────────────────────────────

const WINDOW_LABELS: Record<UptimeWindow, { left: string; right: string }> = {
  '90d': { left: '90 days ago', right: 'Today' },
  '24h': { left: '24 hours ago', right: 'Now' },
  '1h': { left: '1 hour ago', right: 'Now' },
};

function UptimeBlock({
  severity,
  windowSel,
  uptime,
  isLoading = false,
  onCellClick,
}: {
  severity: SuiteSeverity;
  windowSel: UptimeWindow;
  uptime: UptimeWindowResponse | undefined;
  isLoading?: boolean;
  onCellClick: (bucketIdx: number) => void;
}) {
  // Backend may return a non-default bucket density for sparse-cron suites
  // (e.g. transaction-monitor-* uses 5-min buckets in the 1h window). Honor
  // the server's `bucketMs` when present; fall back to the static enum map
  // for older API deployments that don't yet emit the field.
  const windowMs = uptime?.bucketMs ?? bucketMsForWindow(windowSel);
  const expectedCount = uptime
    ? uptime.buckets.length
    : windowSel === '90d'
      ? 90
      : windowSel === '24h'
        ? 96
        : 60;
  // Snapshot taken once per render so all cells judge "pending" against the
  // same clock. React-Query refetch (30s for 1h, 60s elsewhere) gives us a
  // fresh render and a fresh `Date.now()` for free.
  const renderNow = Date.now();

  const rawBuckets = useMemo<WindowBucket[]>(() => {
    if (!uptime) {
      return Array.from(
        { length: expectedCount },
        (): WindowBucket => ({
          startAt: '',
          endAt: '',
          status: 'no_data',
          okCount: 0,
          warnCount: 0,
          downCount: 0,
          totalProbes: 0,
          successPct: 0,
        }),
      );
    }
    return uptime.buckets;
  }, [uptime, expectedCount]);

  // Trailing pending placeholders for slots the backend hasn't reported yet
  // (current minute + just-completed-but-still-ingesting minute). Recomputed
  // every render so the shimmer follows the clock without waiting on refetch.
  const lastRealEnd = rawBuckets.length
    ? rawBuckets[rawBuckets.length - 1].endAt
    : undefined;
  const padNow = trailingPadNow(lastRealEnd, windowMs, renderNow);
  const buckets = padTrailingPending(rawBuckets, windowMs, padNow);

  // An anchored (historical) window shows its actual date range at the foot
  // instead of the live "24 hours ago / Now".
  const anchored =
    !!uptime && renderNow - new Date(uptime.endAt).getTime() > 2 * windowMs;
  const labels = anchored
    ? {
        left: formatWindowEdge(uptime.startAt),
        right: formatWindowEdge(uptime.endAt),
      }
    : WINDOW_LABELS[windowSel];
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const hoveredBucket = hoverIdx !== null ? buckets[hoverIdx] : undefined;
  const hoverLabel = hoveredBucket
    ? formatBucketHoverLabel(hoveredBucket, windowSel)
    : null;

  return (
    <div className="px-4 pb-5 pt-1 sm:px-5 sm:pb-6">
      <div key={windowSel}>
        {/* 1px gaps below 640px: at 2px, 96 cells (24h) need 386px and
            overflowed a 375px screen sideways. */}
        <div
          className="flex h-8 items-stretch gap-px sm:h-9 sm:gap-[2px]"
          role="group"
          aria-label={`${windowSel} uptime — ${SUITE_SEVERITY_META[severity].label}`}
          onMouseOver={(e) => {
            const cell = (e.target as HTMLElement).closest<HTMLElement>(
              '[data-idx]',
            );
            if (!cell) return;
            const idx = Number(cell.dataset['idx']);
            setHoverIdx(idx);
          }}
          onMouseLeave={() => {
            setHoverIdx(null);
          }}
          onFocus={(e) => {
            const cell = (e.target as HTMLElement).closest<HTMLElement>(
              '[data-idx]',
            );
            if (!cell) return;
            const idx = Number(cell.dataset['idx']);
            setHoverIdx(idx);
          }}
          onBlur={() => {
            setHoverIdx(null);
          }}
        >
          {buckets.map((bucket, idx) => {
            const severity = bucketSeverity(bucket, windowSel);
            const tone = bucketTone(severity);
            const marked = shouldMarkFailures(bucket, severity);
            const pending =
              (isLoading && !uptime) ||
              isPendingBucket(
                bucket.startAt,
                bucket.totalProbes,
                windowMs,
                renderNow,
              );
            const key = bucket.startAt || `bucket-${String(idx)}`;
            const hoverText = formatBucketHoverLabel(bucket, windowSel);
            return (
              <button
                key={key}
                type="button"
                data-idx={idx}
                onClick={() => {
                  onCellClick(idx);
                }}
                className={clsx(
                  'relative min-w-px flex-1 cursor-pointer rounded-[2.5px] transition-transform duration-100 hover:scale-y-[1.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                  pending ? 'bar-pending' : TONE_BAR[tone],
                )}
                title={hoverText ?? undefined}
                aria-label={
                  hoverText !== null
                    ? `${hoverText}, open details`
                    : `Bucket ${String(idx + 1)} of ${String(buckets.length)}, open details`
                }
              >
                {marked && <FailureMark />}
              </button>
            );
          })}
        </div>
        <div className="mt-2.5 flex items-center justify-between text-[12px] text-ink-3 [font-feature-settings:'tnum']">
          <span>{labels.left}</span>
          <span className="mx-3 flex-1 border-t border-dashed border-line" />
          {hoverLabel && (
            <>
              <span className="whitespace-nowrap font-semibold text-ink-2">
                {hoverLabel}
              </span>
              <span className="mx-3 flex-1 border-t border-dashed border-line" />
            </>
          )}
          <span>{labels.right}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact per-endpoint uptime histogram. Same visual grammar as the
 * component-level bar in `UptimeBlock` (cells colored by bucket status),
 * minus the overall-pct footer and drill-down.
 *
 * Post-Phase-3 the upstream CI parser writes a row for every expected probe
 * — including a synthesized `down` row when the suite timed out before
 * reaching it — so per-endpoint buckets stay in lockstep with the
 * component-level row without any inference logic on the frontend.
 */
interface HoverState {
  idx: number;
  x: number;
  y: number;
}

const StatusHistoryBar = memo(function StatusHistoryBar({
  buckets,
  windowSel,
  endpointKey,
  bucketMs,
}: {
  buckets: readonly WindowBucket[];
  windowSel: UptimeWindow;
  endpointKey: string;
  bucketMs?: number;
}) {
  const [hover, setHover] = useState<HoverState | null>(null);
  // Honor server's per-suite cadence override (e.g. transaction-monitor
  // emits a 5-min bucket grid for the 1h window). Falling back to the
  // static enum here would render 1-min pending placeholders under
  // 5-min real cells — the bar looks like it ticks at the wrong cadence.
  const windowMs = bucketMs ?? bucketMsForWindow(windowSel);
  const renderNow = Date.now();
  // Extend the per-endpoint timeline with trailing pending slots, same trick
  // as UptimeBlock — but only for live windows. An anchored (historical)
  // window ends in the past, so cap padding at its last bucket to avoid
  // phantom trailing no-data cells.
  const lastRealEnd = buckets.length
    ? buckets[buckets.length - 1].endAt
    : undefined;
  const padNow = trailingPadNow(lastRealEnd, windowMs, renderNow);
  const paddedBuckets = padTrailingPending(buckets, windowMs, padNow);
  const hovered = hover ? paddedBuckets[hover.idx] : null;
  return (
    <>
      <div
        key={windowSel}
        className="flex h-7 items-stretch gap-px sm:gap-[2px]"
        role="img"
        aria-label={`${windowSel} uptime for ${endpointKey}`}
        onMouseOver={(e) => {
          const cell = (e.target as HTMLElement).closest<HTMLElement>(
            '[data-idx]',
          );
          if (!cell) return;
          const idx = Number(cell.dataset['idx']);
          const rect = cell.getBoundingClientRect();
          setHover((prev) =>
            prev && prev.idx === idx
              ? prev
              : { idx, x: rect.left + rect.width / 2, y: rect.top },
          );
        }}
        onMouseLeave={() => {
          setHover(null);
        }}
      >
        {paddedBuckets.map((bucket, idx) => {
          const severity = bucketSeverity(bucket, windowSel);
          const tone = bucketTone(severity);
          const marked = shouldMarkFailures(bucket, severity);
          const pending = isPendingBucket(
            bucket.startAt,
            bucket.totalProbes,
            windowMs,
            renderNow,
          );
          const key = bucket.startAt || `bar-${String(idx)}`;
          return (
            <div
              key={key}
              data-idx={idx}
              className={clsx(
                'relative min-w-px flex-1 rounded-[2.5px]',
                pending ? 'bar-pending' : TONE_BAR[tone],
              )}
            >
              {marked && <FailureMark />}
            </div>
          );
        })}
      </div>
      {hover && hovered && (
        <HoverTooltip x={hover.x} y={hover.y}>
          <StatusBucketTip bucket={hovered} windowSel={windowSel} />
        </HoverTooltip>
      )}
    </>
  );
});

function StatusBucketTip({
  bucket,
  windowSel,
}: {
  bucket: WindowBucket;
  windowSel: UptimeWindow;
}) {
  const tone = bucketTone(bucketSeverity(bucket, windowSel));
  return (
    <>
      <TipHeader tone={tone} />
      <TipTime>{formatBucketTimeRange(bucket, windowSel)}</TipTime>
      {bucket.totalProbes > 0 ? (
        <TipDetail>
          {bucket.okCount} ok · {bucket.warnCount} warn · {bucket.downCount}{' '}
          down · {formatPct(bucket.successPct)}
        </TipDetail>
      ) : (
        <TipDetail>No probes</TipDetail>
      )}
    </>
  );
}

// ─── Skeleton / empty / error ───────────────────────────────────────

function ServicesSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, idx) => (
        <div
          key={`skeleton-${String(idx)}`}
          className="border-b border-line px-4 pb-5 pt-5 last:border-b-0 sm:px-5 sm:pb-6 sm:pt-6"
        >
          <div className="skeleton-shine h-[18px] w-40 rounded" />
          <div className="skeleton-shine mt-2 h-3.5 w-28 rounded" />
          <div className="skeleton-shine mt-5 h-8 rounded sm:h-9" />
        </div>
      ))}
    </>
  );
}

function EmptyBlock() {
  return (
    <div className="px-5 py-12 text-center">
      <p className="text-[16px] font-medium text-ink-1">
        No services reporting yet.
      </p>
      <p className="mt-2 text-[14px] text-ink-3">
        The ingest poller runs every 30 seconds. Health data will appear once
        the first cycle completes.
      </p>
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="bg-down-bg px-5 py-5">
      <p className="text-[16px] font-medium text-down-fg">
        Services unavailable
      </p>
      <p className="mt-1.5 text-[14px] text-ink-2">{message}</p>
    </div>
  );
}

export default App;
